import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import type { SignKeyInfo } from '@/types/SignKeyInfo.ts';
import type { SignDocument } from '@/types/SignDocument.ts';
import type { SignPayload } from '@/types/SignPayload.ts';
import { useMessageStore } from '@/stores/message.ts';
import { SignStatus } from '@/types/enums/SignStatus.ts';
import type { SignAccepted } from '@/types/SignAccepted.ts';
import type { RejectPayload } from '@/types/RejectPayload.ts';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import type { SignaturePosition } from '@/types/SignaturePosition.ts';
import type { ForwardSignee } from '@/types/deepsign/ForwardSignee.ts';
import type { UpdateSignee } from '@/types/deepsign/UpdateSignee.ts';
import type { SignSignee } from '@/types/SignSignee.ts';
import { base64ToFile } from '@/composables/useUtils.ts';
import type { DocumentApproveBody } from '@/types/DocumentApproveBody.ts';
import { useApi } from '@/composables/useApi.ts';
import { onRejected } from '@/api/interceptor.ts';
import i18n from '@/plugins/i18n';
import { getAutographTooBigMessage } from '@/utils/autographErrors';
import AuthService from '@/services/authService.ts';
import { updateDocumentSignee } from '@/api/deepsign/documents-signees.ts';

export const useSignStore = defineStore('sign', () => {
  const { deepSignAPI } = useApi();
  // Message store
  const messageStore = useMessageStore();

  // SignKeyInfo
  const signKeyInfo = ref<SignKeyInfo>();
  const signaturePosition = ref<SignaturePosition>();
  const signaturePlaced = ref(false);
  const pageDetails = ref();

  const userEmail = computed(() => signKeyInfo.value?.email);
  const getSignaturePosition = computed(() => signaturePosition.value);
  const getPlacedSignaturePosition = computed(() => {
    if (placedSignatures.value) {
      return placedSignatures.value.find(
        (sg) => sg.signeeId === userSigneeId.value
      )?.autographPosition;
    } else {
      return undefined;
    }
  });
  const isSignaturePlaced = computed(() => signaturePlaced.value);
  const getPageDetails = computed(() => pageDetails.value);
  const roles = computed(() => signKeyInfo.value?.types);
  const userSignees = computed(
    () =>
      document.value?.signees.filter(
        (signee) => signee.email === userEmail.value
      ) ?? []
  );

  async function fetchSignKeyInfo(signKey: string) {
    try {
      const response = await deepSignAPI.value.signPublicInfo.get(signKey);
      if (response?.data) {
        signKeyInfo.value = response.data as SignKeyInfo;
      }
      return Promise.resolve(response);
    } catch (error) {
      await onRejected(error);
      return Promise.reject(error);
    }
  }

  // Document
  const document: Ref<SignDocument | null> = ref(null);
  const asynchronousSign: Ref<SignAccepted | null> = ref(null);
  const asynchronousSignRequestDateTime: Ref<string> = ref('');

  //SealPreview
  const sealPreviewUrl = ref();

  const isOrdered = computed(() => {
    return (document.value?.signeesOrdered.length ?? 1) > 1;
  });

  const isEnvelope = computed(() => (document.value?.files?.length ?? 0) > 1);

  const isSignee = computed(() => {
    return document.value?.signContext.email !== null || false;
  });
  const placedSignatures = computed(
    () =>
      document.value?.signees.filter(
        (signee) => signee.autographPosition !== null
      ) || []
  );
  const userSigneeId = computed(
    () => document.value?.signContext.signeeId ?? ''
  );

  async function fetchSignDocument(documentId: string) {
    try {
      const response = await deepSignAPI.value.documents.getById(documentId);
      if (response.data) {
        document.value = response.data as unknown as SignDocument;
      }
      return Promise.resolve(response);
    } catch (error) {
      await onRejected(error);
      return Promise.reject(error);
    }
  }

  async function fetchDocumentSign(documentId: string) {
    try {
      const response = await deepSignAPI.value.documents.getById(documentId);
      if (response.data) {
        document.value = response.data as unknown as SignDocument;
        return Promise.resolve(response.data);
      }
    } catch (error) {
      await onRejected(error);
      return Promise.reject(error);
    }
  }

  // Sign Document
  const signPending = ref(false);
  const mobileIdCheckPending = ref(false);

  async function sealDocument(documentId: string, payload) {
    try {
      const seal = JSON.stringify({
        autographPosition: payload.sign.autographPosition ?? null,
        annotation: payload.annotation ?? null,
        sealId: payload.sealId
      });

      const formData = new FormData();
      const jsonPart = new Blob([seal], { type: 'application/json' });

      formData.append('seal', jsonPart);
      formData.append('data', jsonPart);

      const response = await deepSignAPI.value.documentsSeal.create(
        documentId,
        // types declare JSON body, but runtime accepts FormData.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formData as any
      );

      document.value = response.data;
      return response;
    } catch (error) {
      await onRejected(error);
      return Promise.reject(error);
    }
  }

  async function signDocument(documentId: string, payload: SignPayload) {
    try {
      const sign = JSON.stringify({
        comment: payload.sign.comment ?? '',
        autographPosition: payload.sign.autographPosition ?? null,
        authorityService: payload.sign.authorityService ?? null
      });
      // Authenticated users' autograph is already persisted in the BE. Guests have no persisted autograph and must always send it.
      const autograph = !AuthService.isAuthenticated
        ? payload?.autograph || null
        : null;
      const formData = new FormData();
      formData.append('sign', new Blob([sign], { type: 'application/json' }));

      if (autograph) {
        formData.append('autograph', autograph);
      }

      const filesCount = document.value?.files?.length ?? 0;

      const response = await deepSignAPI.value.documentsSign.create(
        documentId,
        formData
      );
      let documentTmp;
      if (response.status === 202) {
        asynchronousSign.value = response.data;
        asynchronousSignRequestDateTime.value = new Date().toISOString();
        documentTmp = document.value;
      } else {
        documentTmp = response.data;
      }
      if (documentTmp.signStatus === SignStatus.SIGNED) {
        messageStore.showMessage({
          text: isEnvelope.value
            ? i18n.global.t('snackbars.signed_text_envelope', {
                n: documentTmp.files?.length ?? filesCount
              })
            : i18n.global.t('snackbars.signed_text'),
          color: 'success',
          icon: 'fa fa-circle-check'
        });
      }
      document.value = documentTmp;
    } catch (error) {
      signPending.value = false;
      return Promise.reject(error);
    }
  }

  async function fetchAuthSignStatus(signKey: string, documentId: string) {
    try {
      const response = await deepSignAPI.value.documentsStatus.get(signKey);
      if (document.value) {
        document.value.signContext.signStatus = response.data
          .signStatus as SignStatus;
        document.value.signContext.lastError = response.data.lastError;
      }
      if (response.data.signStatus === SignStatus.SIGNED) {
        await fetchSignDocument(documentId);
      }
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Default autograph data

  // Reject Signature
  const rejectSignaturePending = ref(false);

  // Approve or Reject signature process
  const approvingSignatureProcess = ref(false);

  async function rejectSignature(
    payload: RejectPayload,
    documentId: string,
    isApproverCase: boolean = false
  ) {
    try {
      rejectSignaturePending.value = true;
      const response = await deepSignAPI.value.documentsReject.update(
        documentId,
        {
          comment: payload.comment
        }
      );
      const signeeMessageConfig = {
        key: 'snackbars.signature_rejected',
        color: 'success',
        icon: 'fa fa-circle-check'
      };
      const approverMessageConfig = {
        ...signeeMessageConfig,
        key: isEnvelope.value
          ? 'snackbars.document_rejected_by_approver_envelope'
          : 'snackbars.document_rejected_by_approver'
      };
      const messageConfig = isApproverCase
        ? approverMessageConfig
        : signeeMessageConfig;

      rejectSignaturePending.value = false;
      messageStore.showMessage(messageConfig);
      rejectSignaturePending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      const signeeErrorMessageConfig = {
        key: 'errors.api.reject_signature',
        color: 'error',
        icon: 'fa fa-circle-exclamation'
      };
      const approverErrorMessageConfig = {
        ...signeeErrorMessageConfig,
        key: 'errors.api.signature_process_reject_error'
      };
      const messageConfig = isApproverCase
        ? approverErrorMessageConfig
        : signeeErrorMessageConfig;
      messageStore.showMessage(messageConfig);
      rejectSignaturePending.value = false;
      return Promise.reject(error);
    }
  }

  async function approveSignProcess(
    documentId: string,
    payload: DocumentApproveBody
  ) {
    try {
      approvingSignatureProcess.value = true;

      const response = await deepSignAPI.value.documentsApprove.create(
        documentId,
        payload
      );
      document.value = response.data as SignDocument;

      approvingSignatureProcess.value = false;
      messageStore.showMessage({
        key: isEnvelope.value
          ? 'snackbars.signature_process_approved_envelope'
          : 'snackbars.signature_process_approved',
        color: 'success',
        icon: 'fa fa-circle-check'
      });
    } catch (error) {
      rejectSignaturePending.value = false;
      messageStore.showMessage({
        key: 'errors.api.signature_process_approved_error',
        color: 'error',
        icon: 'fa fa-circle-exclamation'
      });
    }
  }

  // SID
  const resendSidPending = ref(false);

  async function resendSid(signKey: string) {
    try {
      resendSidPending.value = true;
      const response =
        await deepSignAPI.value.signPublicResendSid.create(signKey);
      resendSidPending.value = false;
      return Promise.resolve(response);
    } catch (error) {
      resendSidPending.value = false;
      messageStore.showMessage({
        key: 'errors.api.send_email',
        color: 'error',
        icon: 'fa fa-circle-exclamation'
      });
      return Promise.reject(error);
    }
  }

  // Attachments
  async function postAttachment(documentId: string, file: File) {
    try {
      const response =
        await deepSignAPI.value.documentsSigneeAttachments.create(
          documentId,
          file
        );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function deleteAttachment(
    documentId: string,
    signeeAttachementId: string
  ) {
    try {
      const response =
        await deepSignAPI.value.documentsSigneeAttachments.deleteById(
          documentId,
          signeeAttachementId
        );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function enableAuthorityService(authorityService: AuthorityService) {
    if (document.value) {
      document.value.signContext.availableAuthorityServices[authorityService] =
        {
          hasRequiredLevel: true,
          signeeIssues: []
        };
    }
  }

  async function forward(documentId: string, payload: ForwardSignee) {
    return deepSignAPI.value.documentsForward.create(documentId, payload);
  }

  async function addNewSignee(documentId: string, payload: ForwardSignee) {
    const response = await deepSignAPI.value.documentsSignees.create(
      documentId,
      payload
    );
    document.value?.signees.push(response.data);
  }

  async function removeSignee(documentId: string, signeeId: string) {
    await deepSignAPI.value.documentsSignees.deleteById(documentId, signeeId);
    if (document.value?.signees) {
      document.value.signees = document.value.signees.filter(
        (signee) => signee.signeeId !== signeeId
      );
      if (document.value.signeesOrdered.length > 1) {
        removeOrderedSigneeById(document.value.signeesOrdered, signeeId);
      }
    }
  }

  function removeOrderedSigneeById(
    signeesOrdered: SignSignee[][],
    signeeId: string
  ) {
    for (let i = 0; i < signeesOrdered.length; i++) {
      const innerArray = signeesOrdered[i];
      const index = innerArray.findIndex(
        (signee) => signee.signeeId === signeeId
      );

      if (index !== -1) {
        innerArray.splice(index, 1);

        if (innerArray.length === 0) {
          signeesOrdered.splice(i, 1);
          i--;
        }
      }
    }
  }

  async function updateSigneeEmail(
    documentId: string,
    signeeId: string,
    payload: UpdateSignee
  ) {
    const response = await deepSignAPI.value.documentsSignees.updateById(
      documentId,
      signeeId,
      payload
    );
    if (document.value?.signees) {
      const signeeIndex = document.value.signees.findIndex(
        (signee) => signee.signeeId === signeeId
      );
      if (signeeIndex >= 0) {
        document.value.signees[signeeIndex] = response.data;
      }
    }
  }

  // Persists a placeholder placement change (resize/move/delete/auto-place)
  // made on the sign step for an envelope. Uses the local `UpdateSignee`
  // payload type (unlike `updateSigneeEmail` above, which goes through the
  // generated SDK) since only it supports `fileAutographPositions`, the map
  // needed to update a single file's positions without dropping every other
  // file's already-placed ones — mirrors `createStore.updateInvitee`.
  async function updateSignee(
    documentId: string,
    signeeId: string,
    payload: Partial<UpdateSignee>
  ) {
    // `comment` is only allowed while the document is still in DRAFT status
    // (see the SDK's `UpdateSignee` type) — strip it here so every caller of
    // this function is safe by construction, even if it reused a
    // `buildSigneeUpdatePayload()` payload that was built for the draft flow.
    const { comment, ...signPayload } = payload;
    const response = await updateDocumentSignee(
      documentId,
      signeeId,
      signPayload
    );
    updateSigneeInLocalData(response.data as SignSignee);
    return response;
  }

  // Mirrors `updateInviteeInLocalData` in the create store: keeps every local
  // representation of a signee in sync with the backend response after a
  // placement change — the flat (deprecated) `signees` list, the per-signOrder
  // `signeesOrdered` groups and, since the document may be an envelope, the
  // per-group `signeeGroups[].signees[]` read by the placement rebuild for
  // `signings`.
  function updateSigneeInLocalData(signee: SignSignee) {
    if (!document.value) return;

    const signeeIndex = document.value.signees.findIndex(
      (candidate) => candidate.signeeId === signee.signeeId
    );
    if (signeeIndex >= 0) {
      document.value.signees[signeeIndex] = signee;
    }

    document.value.signeesOrdered.forEach((group) => {
      const match = group.find(
        (candidate) => candidate.signeeId === signee.signeeId
      );
      if (match) {
        match.email = signee.email;
        match.autographPosition = signee.autographPosition;
        match.signings = signee.signings;
        match.signOrder = signee.signOrder;
        match.policy = signee.policy;
      }
    });

    document.value.signeeGroups?.forEach((group) => {
      const match = group.signees?.find(
        (candidate) => candidate.signeeId === signee.signeeId
      );
      if (match) {
        match.email = signee.email;
        match.autographPosition = signee.autographPosition;
        match.signings = signee.signings;
        match.signOrder = signee.signOrder;
        match.policy = signee.policy;
      }
    });
  }

  function removeObserver(observerId: string) {
    if (document.value) {
      const observerIndex = document.value.observers.findIndex(
        (observer) => observer.observerId === observerId
      );
      if (observerIndex >= 0) {
        document.value?.observers.splice(observerIndex, 1);
      }
    }
  }

  // Signature Preview
  const autographData = ref<string | null>(null);
  const previewUrl = ref<string | null>(null);

  // Accepts an explicit value so callers can validate a candidate autograph
  // against the BE *before* committing it as the current autographData -
  // falls back to the current autographData when called without one.
  async function updateAutographImage(newAutographData?: string | null) {
    const dataToUpload = newAutographData ?? autographData.value;
    if (dataToUpload && !dataToUpload.startsWith('https')) {
      const fileImage = await base64ToFile(dataToUpload, 'autograph');
      try {
        await deepSignAPI.value.usersMeAutograph.create(fileImage);
        return true;
      } catch (error) {
        const tooBigMessage = await getAutographTooBigMessage(error);
        if (tooBigMessage) {
          messageStore.showMessage({
            text: i18n.global.t(tooBigMessage.key, tooBigMessage.params),
            color: 'error',
            icon: 'fa fa-circle-exclamation',
            timeout: 8000
          });
        }
        return false;
      }
    }
    return true;
  }

  async function getSignaturePreview(documentId: string) {
    const autograph = autographData.value
      ? await base64ToFile(autographData.value, 'autograph')
      : null;
    try {
      const response = await deepSignAPI.value.documentsSignaturePreview.create(
        documentId,
        {
          sizeFactor: 3,
          optionalText: undefined,
          location: undefined
        },
        autograph
      );
      previewUrl.value = URL.createObjectURL(response.data);
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Error while getting Signature preview ', error);
      return null;
    }
  }

  return {
    // SignKeyInfo
    signKeyInfo,
    userEmail,
    roles,
    fetchSignKeyInfo,

    // Document
    document,
    isSignee,
    placedSignatures,
    userSigneeId,
    userSignees,
    getPlacedSignaturePosition,
    fetchSignDocument,
    fetchDocumentSign,

    // Sign Document
    signPending,
    mobileIdCheckPending,
    asynchronousSign,
    signDocument,
    fetchAuthSignStatus,
    signaturePosition,
    getSignaturePosition,
    signaturePlaced,
    isSignaturePlaced,
    pageDetails,
    getPageDetails,
    asynchronousSignRequestDateTime,
    isOrdered,
    isEnvelope,
    sealDocument,

    // add/forward/update/delete
    forward,
    addNewSignee,
    removeSignee,
    updateSigneeEmail,
    updateSignee,
    updateSigneeInLocalData,

    // Observers
    removeObserver,

    // Reject Signature
    rejectSignaturePending,
    rejectSignature,

    //Approve signature process
    approveSignProcess,
    approvingSignatureProcess,

    // SID
    resendSidPending,
    resendSid,

    // Attachments
    postAttachment,
    deleteAttachment,

    // Enable authority service
    enableAuthorityService,

    //Preview
    autographData,
    previewUrl,
    getSignaturePreview,
    updateAutographImage,

    //Seal
    sealPreviewUrl
  };
});
